// src/components/Modals/DisposeAssetModal.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Box, Button,
  Container, Card, CardContent, Stack, TextField, MenuItem,
  CircularProgress, Snackbar, Alert, Chip, Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Slide } from '@mui/material';
import CloseIcon           from '@mui/icons-material/Close';
import DeleteForeverIcon   from '@mui/icons-material/DeleteForever';
import HistoryIcon         from '@mui/icons-material/History';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';
import BlockIcon           from '@mui/icons-material/Block';
import ErrorOutlineIcon    from '@mui/icons-material/ErrorOutline';
import QrCode2Icon         from '@mui/icons-material/QrCode2';
import LayersIcon          from '@mui/icons-material/Layers';
import InventoryIcon       from '@mui/icons-material/Inventory';

import { assetService } from "../../services/assetService";
import { singleValueListService } from "../../services/singleValueListService";


// ── HELPERS ───────────────────────────────────────────────────────────────
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateSafely = (val) => {
  if (!val) return null;
  if (typeof val?.toDate === 'function') return val.toDate();
  if (val?.seconds !== undefined) return new Date(val.seconds * 1000);
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [year, month, day] = val.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (val) => {
  const d = parseDateSafely(val);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateTime = (val) => {
  const d = parseDateSafely(val);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getInitials = (name = '') => {
  if (!name || typeof name !== 'string') return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  return parts.map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

const isDisposalBlocked = (assetType) => {
  if (!assetType) return false;
  const blockedTypes = [
    'consumable', 'consumables', 'spare part', 'spare parts', 'sparepart', 'spareparts',
  ];
  return blockedTypes.includes(String(assetType).toLowerCase().trim());
};

const isSerializedAsset = (asset) => {
  if (!asset) return false;
  if (asset.serializationType) {
    return asset.serializationType.toLowerCase() === 'serialized';
  }
  if (typeof asset.isSerialized === 'boolean') return asset.isSerialized;
  if (typeof asset.isSerialized === 'string') {
    return asset.isSerialized.toLowerCase() === 'true' || asset.isSerialized.toLowerCase() === 'yes';
  }
  return asset.quantity === undefined || asset.quantity === null || Number(asset.quantity) <= 1;
};

const getTotalDisposed = (history = []) => {
  if (!Array.isArray(history)) return 0;
  return history.reduce((acc, curr) => acc + Number(curr.disposedQuantity || 1), 0);
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ── STYLED COMPONENTS ─────────────────────────────────────────────────────
const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b',
  marginBottom: '1.25rem', paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.8125rem', fontWeight: 600, color: '#374151',
  textTransform: 'uppercase', letterSpacing: '0.025em',
  marginBottom: '0.375rem',
}));

const FormGroup = styled(Box)(() => ({
  flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column',
  gap: '0.375rem',
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    '& fieldset':        { borderColor: '#d1d5db' },
    '&:hover fieldset':  { borderColor: '#dc2626' },
    '&.Mui-focused fieldset': {
      borderColor: '#dc2626',
      boxShadow:   '0 0 0 3px rgba(220,38,38,0.12)',
    },
  },
}));

const FormRow = styled(Box)(() => ({
  display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap',
  '&:last-child': { marginBottom: 0 },
}));

const HistoryEntry = styled(Box)(() => ({
  display: 'flex', gap: '1rem', alignItems: 'flex-start',
  padding: '1rem', borderRadius: '8px',
  background: '#fff5f5', border: '1px solid #fecaca',
}));

const cancelBtnSx = {
  px: 3, py: 1, backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #d1d5db', borderRadius: '6px', fontWeight: 500,
  textTransform: 'none', minWidth: '100px', height: '40px',
  '&:hover':    { backgroundColor: '#e5e7eb' },
  '&:disabled': { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1, backgroundColor: '#dc2626', color: '#fff',
  borderRadius: '6px', fontWeight: 600, textTransform: 'none',
  minWidth: '160px', height: '40px',
  display: 'flex', alignItems: 'center', gap: 1,
  '&:hover':    { backgroundColor: '#b91c1c' },
  '&:disabled': { backgroundColor: '#fca5a5' },
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────
const DisposeAssetModal = ({
  open,
  asset       = null,
  initialTab  = 'dispose', // 'dispose' | 'history'
  onClose,
  onSave,
  onError,
  currentUser = null,
}) => {
  // ── Form State ────────────────────────────────────────────────────────────
  const [disposalDate,    setDisposalDate]    = useState(getLocalDateString());
  const [disposalReason,  setDisposalReason]  = useState('');
  const [customReason,    setCustomReason]    = useState('');
  const [disposeQuantity, setDisposeQuantity] = useState(1);

  // ── UI / Interaction State ────────────────────────────────────────────────
  const [activeTab,       setActiveTab]       = useState('dispose');
  const [loading,         setLoading]         = useState(false);
  const [submitError,     setSubmitError]     = useState('');
  const [touched,         setTouched]         = useState({ reason: false, custom: false, qty: false });
   const [historyItems,    setHistoryItems]    = useState([]);
  const [disposalReasons, setDisposalReasons] = useState([]);
  const [reasonsLoading,  setReasonsLoading]  = useState(false);

  const formBodyRef = useRef(null);

  const blocked      = isDisposalBlocked(asset?.assetType);
  const isSerialized = isSerializedAsset(asset);
 const isAlreadyDisposed =
  String(asset?.status || '').toLowerCase() === 'disposed';

  // ── Seed Modal on Open ────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !asset) return;

    setDisposalDate(getLocalDateString());
    setDisposalReason('');
    setCustomReason('');
    setDisposeQuantity(1);
    setSubmitError('');
    setTouched({ reason: false, custom: false, qty: false });
    setLoading(false);

    // If asset is already fully disposed or blocked, default to history tab
    if (isAlreadyDisposed && (!initialTab || initialTab === 'dispose')) {
      setActiveTab('history');
    } else {
      setActiveTab(initialTab || 'dispose');
    }

    const history = Array.isArray(asset.disposalHistory)
      ? [...asset.disposalHistory].reverse()
      : [];
    setHistoryItems(history);
  }, [open, asset, initialTab, isAlreadyDisposed]);
  // ── Load disposal reasons from the DB catalog ─────────────────────────────
  
  useEffect(() => {
    if (!open || !currentUser?.companyId) return;
    let cancelled = false;
    setReasonsLoading(true);

    (async () => {
      try {
        const items = await singleValueListService.getItems("disposal_reasons", {
          companyId: currentUser.companyId,
        });
        if (cancelled) return;
        setDisposalReasons(items || []);

        // ── Default the reason once the catalog is available ──
        // Only set it if the user hasn't already picked something.
        setDisposalReason((prev) => {
          if (prev) return prev;

          // Prefer "Other" so the user has to type specifics.
          const otherEntry = (items || []).find(
            (it) => String(it.value || "").toLowerCase() === "other",
          );
          // Fall back to the first entry in the catalog.
          return otherEntry?.value || items?.[0]?.value || "";
        });
      } catch (err) {
        console.warn("Disposal reasons load failed:", err);
        if (!cancelled) setDisposalReasons([]);
      } finally {
        if (!cancelled) setReasonsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, currentUser?.companyId]);
  // ── Derived Validation ────────────────────────────────────────────────────
  const quantityLimit = Number(asset?.quantity || 1);
  const isQtyInvalid = !isSerialized && (
    !disposeQuantity || 
    isNaN(disposeQuantity) || 
    disposeQuantity <= 0 || 
    disposeQuantity > quantityLimit
  );

 const customReasonMissing =
  String(disposalReason || '').toLowerCase() === 'other' && !customReason.trim();
  const formInvalid = !disposalDate || !disposalReason || customReasonMissing || isQtyInvalid;
  const canSubmit = !loading && !formInvalid && !isAlreadyDisposed;

  const totalDisposedCount = getTotalDisposed(asset?.disposalHistory);

  // ── Submit: Dispose Asset ─────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;

    setLoading(true);
    setSubmitError('');

    try {
     const finalReason =
  String(disposalReason || '').toLowerCase() === 'other'
    ? `Other: ${customReason.trim()}`
    : disposalReason;

      const finalQty = isSerialized ? 1 : Number(disposeQuantity);

      const updated = await assetService.disposeAsset(
        asset.id,
        {
          disposalDate,
          disposalReason: finalReason,
          disposalRemarks: customReason.trim(),
          disposeQuantity: finalQty,
        },
        currentUser?.email
      );

      if (onSave)  onSave(updated);
      if (onError) onError('Asset disposal recorded successfully.', 'success');
      onClose();
    } catch (err) {
      console.error('[DisposeAssetModal] Disposal failed:', err);
      setSubmitError(err.message || 'Failed to dispose asset. Please try again.');
      if (formBodyRef.current) {
        formBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setLoading(false);
    }
  }, [
    canSubmit, asset, disposalDate, disposalReason, customReason,
    disposeQuantity, isSerialized, currentUser, onSave, onError, onClose
  ]);

  if (!asset) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!loading ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          width: '100vw', height: '100vh',
          maxWidth: '100vw', maxHeight: '100vh',
          margin: 0, boxSizing: 'border-box',
        },
      }}
    >
      {/* ── APP BAR ──────────────────────────────────────────────────────── */}
      <AppBar sx={{ position: 'relative', flexShrink: 0, bgcolor: blocked ? '#475569' : '#dc2626' }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
              {blocked ? 'Disposal Blocked' : 'Dispose Asset'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {asset.assetId} — {asset.assetName}
            </Typography>
          </Box>

          {/* Tab Switcher */}
          <Box sx={{ display: 'flex', gap: 1, mr: 1 }}>
            {[
              { key: 'dispose', label: 'Dispose', icon: <DeleteForeverIcon sx={{ fontSize: '1rem' }} /> },
              { key: 'history', label: 'History', icon: <HistoryIcon       sx={{ fontSize: '1rem' }} /> },
            ].map(({ key, label, icon }) => (
              <Button
                key={key}
                onClick={() => setActiveTab(key)}
                startIcon={icon}
                sx={{
                  color:       activeTab === key ? '#fff' : 'rgba(255,255,255,0.7)',
                  fontWeight:  activeTab === key ? 700   : 400,
                  textTransform: 'none', fontSize: '0.8125rem',
                  borderBottom: activeTab === key ? '2px solid #fff' : '2px solid transparent',
                  borderRadius: 0, px: 1.5,
                }}
              >
                {label}
                {key === 'history' && historyItems.length > 0 && (
                  <Chip
                    label={historyItems.length}
                    size="small"
                    sx={{
                      ml: 0.75, height: 18, fontSize: '0.65rem',
                      bgcolor: 'rgba(255,255,255,0.25)', color: '#fff',
                    }}
                  />
                )}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── BODY ──────────────────────────────────────────────────────────── */}
      <Box
        ref={formBodyRef}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: '#f1f5f9', py: 4 }}
      >
        <Container maxWidth="md">
          <Stack spacing={3}>

            {/* ── Asset Summary Profile ───────────────────────────────────── */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Asset Profile</SectionTitle>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>

                  {/* Identification */}
                  <Box sx={{
                    flex: 1, minWidth: 200, p: 2, borderRadius: 2,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Identification
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <QrCode2Icon sx={{ color: '#475569', fontSize: '1.1rem' }} />
                      <Typography sx={{
                        fontSize: '0.9375rem', fontWeight: 700, color: '#334155',
                        fontFamily: 'monospace',
                      }}>
                        {asset.manufacturerSerialNumber || asset.assetId || '—'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status / Tracking */}
                  <Box sx={{
                    flex: 1, minWidth: 200, p: 2, borderRadius: 2,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Tracking & Status
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={isSerialized ? 'Serialized' : 'Quantity-Based'}
                        size="small"
                        icon={<LayersIcon />}
                        sx={{
                          fontWeight: 600, fontSize: '0.75rem',
                          bgcolor: isSerialized ? '#eff6ff' : '#f0fdf4',
                          color: isSerialized ? '#1e73be' : '#15803d',
                        }}
                      />
                      <Chip
  label={asset.status || '—'}
  size="small"
  sx={{
    fontWeight: 700, fontSize: '0.75rem',
    bgcolor: isAlreadyDisposed ? '#fee2e2' : '#f3f4f6',
    color: isAlreadyDisposed ? '#991b1b' : '#374151',
  }}
/>
                    </Box>
                  </Box>

                  {/* Current Available Stock */}
                  {!isSerialized && (
                    <Box sx={{
                      flex: 1, minWidth: 200, p: 2, borderRadius: 2,
                      background: '#f8fafc', border: '1px solid #e2e8f0',
                    }}>
                      <Typography sx={{
                        fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                        textTransform: 'uppercase', mb: 0.5,
                      }}>
                        In-Stock Pool
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <InventoryIcon sx={{ color: '#059669', fontSize: '1.1rem' }} />
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#065f46' }}>
                          {quantityLimit} <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#6b7280' }}>units</span>
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Total Disposed Count */}
                  <Box sx={{
                    flex: 1, minWidth: 200, p: 2, borderRadius: 2,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Disposal Events
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#991b1b' }}>
                      {historyItems.length} <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#6b7280' }}>({totalDisposedCount} units total)</span>
                    </Typography>
                  </Box>

                </Box>
              </CardContent>
            </SectionCard>

            {/* ══════════════════════════════════════════════════════════════
                TAB 1: DISPOSE
            ══════════════════════════════════════════════════════════════ */}
            {activeTab === 'dispose' && (
              <>
                {/* Case A: FSD §9 Disposal Blocked Flow */}
                {blocked ? (
                  <SectionCard variant="outlined" sx={{ borderColor: '#ef4444' }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                        <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 48, height: 48 }}>
                          <BlockIcon fontSize="medium" />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>
                            Disposal Not Permitted (FSD §9)
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Consumables and Spare Parts are restricted from structural scrap/disposal operations.
                          </Typography>
                        </Box>
                      </Box>

                      <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Recommended Action:
                        </Typography>
                        <Typography variant="body2">
                          Use the <strong>Consumptions</strong> module to record items consumed. Inventory will reconcile automatically without altering asset lifecycle definitions.
                        </Typography>
                      </Alert>
                    </CardContent>
                  </SectionCard>
                ) : isAlreadyDisposed ? (
                  /* Case B: Already Disposed Banner */
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Avatar sx={{ bgcolor: '#fee2e2', color: '#dc2626', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                        <DeleteForeverIcon fontSize="large" />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>
                        Asset is Fully Disposed
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5, maxWidth: 450, mx: 'auto' }}>
                        This asset has already been decommissioned and written off. All remaining inventory is zero. Please view the History tab to audit past disposal records.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={() => setActiveTab('history')}
                        sx={{ mt: 2, textTransform: 'none', color: '#dc2626', borderColor: '#fca5a5' }}
                      >
                        View Disposal History
                      </Button>
                    </CardContent>
                  </SectionCard>
                ) : (
                  /* Case C: Normal Disposal Form */
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>Disposal Details</SectionTitle>

                      {/* Warning */}
                      <Box sx={{
                        p: 2, mb: 3, borderRadius: 2,
                        background: '#fff5f5', border: '1px solid #fecaca',
                        display: 'flex', alignItems: 'flex-start', gap: 1.5,
                      }}>
                        <WarningAmberIcon sx={{ color: '#dc2626', fontSize: '1.3rem', mt: 0.2 }} />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b', mb: 0.5 }}>
                            Irreversible Action
                          </Typography>
                          <Typography sx={{ fontSize: '0.8125rem', color: '#b91c1c' }}>
                            {isSerialized
                              ? 'Executing this write-off marks this asset as permanently decommissioned. Active assignments will be severed.'
                              : `This action permanently removes up to ${quantityLimit} unit(s) from active inventory. Decommissioning all units sets asset status to "Disposed".`}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Disposal Date */}
                      <FormRow>
                        <FormGroup>
                          <FieldLabel>
                            Disposal Date <span style={{ color: '#ef4444' }}>*</span>
                          </FieldLabel>
                          <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={disposalDate}
                            onChange={(e) => setDisposalDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: getLocalDateString() }}
                            required
                          />
                        </FormGroup>
                      </FormRow>

                      {/* Quantity (Non-Serialized) */}
                      {!isSerialized && (
                        <FormRow>
                          <FormGroup>
                            <FieldLabel>
                              Quantity to Dispose <span style={{ color: '#ef4444' }}>*</span>
                            </FieldLabel>
                            <TextField
                              type="number"
                              size="small"
                              fullWidth
                              value={disposeQuantity}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                setDisposeQuantity(isNaN(val) ? '' : val);
                              }}
                              onBlur={() => setTouched(prev => ({ ...prev, qty: true }))}
                              error={touched.qty && isQtyInvalid}
                              helperText={
                                touched.qty && isQtyInvalid
                                  ? `Please enter a quantity between 1 and ${quantityLimit}.`
                                  : `Available in stock: ${quantityLimit} Units`
                              }
                              inputProps={{ min: 1, max: quantityLimit }}
                              required
                            />
                          </FormGroup>
                        </FormRow>
                      )}

                      {/* Disposal Reason */}
                      <FormRow>
                        <FormGroup>
                          <FieldLabel>
                            Reason for Disposal <span style={{ color: '#ef4444' }}>*</span>
                          </FieldLabel>
                          <TextField
  select
  size="small"
  fullWidth
  value={disposalReason}
  onChange={(e) => setDisposalReason(e.target.value)}
  onBlur={() => setTouched(prev => ({ ...prev, reason: true }))}
  error={touched.reason && !disposalReason}
  helperText={
    touched.reason && !disposalReason
      ? 'Please select a disposal reason.'
      : reasonsLoading
        ? 'Loading reasons…'
        : disposalReasons.length === 0
          ? 'No disposal reasons configured for this company.'
          : ''
  }
  disabled={reasonsLoading || disposalReasons.length === 0}
  required
>
  {disposalReasons.map((option) => (
    <MenuItem key={option.value} value={option.value}>
      {option.label}
    </MenuItem>
  ))}
</TextField>
                        </FormGroup>
                      </FormRow>

                      {/* Custom Reason */}
                      {String(disposalReason || '').toLowerCase() === 'other' && (
                        <FormRow>
                          <FormGroup>
                            <FieldLabel>
                              Specific Reason Details <span style={{ color: '#ef4444' }}>*</span>
                            </FieldLabel>
                            <TextField
                              multiline
                              rows={3}
                              size="small"
                              fullWidth
                              placeholder="Provide details, scrap certificate ID, recycler reference…"
                              value={customReason}
                              onChange={(e) => setCustomReason(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, custom: true }))}
                              error={touched.custom && customReasonMissing}
                              helperText={touched.custom && customReasonMissing ? 'Please specify the reason.' : ''}
                              required
                            />
                          </FormGroup>
                        </FormRow>
                      )}

                    </CardContent>
                  </SectionCard>
                )}
              </>
            )}

            {/* ══════════════════════════════════════════════════════════════
                TAB 2: HISTORY
            ══════════════════════════════════════════════════════════════ */}
            {activeTab === 'history' && (
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Disposal Audit History</SectionTitle>

                  {historyItems.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6, color: '#6b7280' }}>
                      <HistoryIcon sx={{ fontSize: '3rem', opacity: 0.3, mb: 1 }} />
                      <Typography sx={{ fontWeight: 600 }}>
                        No disposal history recorded
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                        Decommission and write-off events will appear here once executed.
                      </Typography>
                    </Box>
                  )}

                  {historyItems.length > 0 && (
                    <Stack spacing={2}>
                      {historyItems.map((entry, idx) => (
                        <HistoryEntry key={entry.id || idx}>
                          <Avatar
                            sx={{
                              width: 40, height: 40, flexShrink: 0,
                              bgcolor: '#fecaca', color: '#991b1b',
                              fontSize: '0.875rem', fontWeight: 700,
                            }}
                          >
                            {getInitials(entry.disposedBy || 'Disposal')}
                          </Avatar>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{
                              display: 'flex', alignItems: 'center',
                              gap: 1, flexWrap: 'wrap', mb: 0.5,
                            }}>
                              <Chip
  label={
    disposalReasons.find(
      (r) => String(r.value).toLowerCase() === String(entry.disposalReason || '').toLowerCase()
    )?.label
    || entry.disposalReason
    || 'Disposed'
  }
  size="small"
  sx={{
    bgcolor: '#fee2e2', color: '#991b1b',
    fontWeight: 700, fontSize: '0.7rem',
    border: '1px solid #fecaca',
  }}
/>
                              <Chip
                                label={`${entry.disposedQuantity || 1} ${entry.disposedQuantity === 1 ? 'Unit' : 'Units'}`}
                                size="small"
                                sx={{
                                  bgcolor: '#f3f4f6', color: '#374151',
                                  fontWeight: 600, fontSize: '0.7rem',
                                }}
                              />
                              <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                {formatDate(entry.disposalDate)}
                              </Typography>
                              {idx === 0 && (
                                <Chip
                                  label="Latest" size="small"
                                  sx={{
                                    bgcolor: '#fef9c3', color: '#713f12',
                                    fontSize: '0.65rem', fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>

                            <Typography sx={{
                              fontWeight: 700, fontSize: '0.9375rem', color: '#1f2937',
                            }}>
                              Disposed by: {entry.disposedBy || '—'}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                              {entry.remainingQuantity !== undefined && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  Remaining Balance:{' '}
                                  <strong>{entry.remainingQuantity} units</strong>
                                </Typography>
                              )}
                              {entry.disposedAt && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  Recorded At:{' '}
                                  <strong>{formatDateTime(entry.disposedAt)}</strong>
                                </Typography>
                              )}
                            </Box>

                            {entry.disposalRemarks && (
                              <Box sx={{
                                mt: 1, p: 1,
                                bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1,
                              }}>
                                <Typography sx={{
                                  fontSize: '0.8125rem', color: '#374151',
                                  fontStyle: 'italic',
                                }}>
                                  "{entry.disposalRemarks}"
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </HistoryEntry>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </SectionCard>
            )}

          </Stack>
        </Container>
      </Box>

      {/* ── STICKY FOOTER ────────────────────────────────────────────────── */}
      <Box sx={{
        flexShrink: 0, width: '100%', height: '72px',
        borderTop: '1px solid #e2e8f0', backgroundColor: '#fff',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'center',
        justifyContent: 'flex-end', gap: 1.5, px: 3,
      }}>
        <Button onClick={onClose} disabled={loading} sx={cancelBtnSx}>
          {blocked || isAlreadyDisposed || activeTab === 'history' ? 'Close' : 'Cancel'}
        </Button>
        {!blocked && !isAlreadyDisposed && activeTab === 'dispose' && (
          <Button onClick={handleSubmit} disabled={!canSubmit} sx={submitBtnSx}>
            {loading && <CircularProgress size={16} color="inherit" />}
            {loading ? 'Processing…' : (
              <>
                <DeleteForeverIcon sx={{ fontSize: '1.25rem' }} />
                {!isSerialized && disposeQuantity < quantityLimit
                  ? `Dispose ${disposeQuantity || 0} Units`
                  : 'Confirm Disposal'}
              </>
            )}
          </Button>
        )}
      </Box>

      {/* ── ERROR SNACKBAR ────────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={6000}
        onClose={() => setSubmitError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setSubmitError('')}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{ width: '100%', minWidth: 400, fontSize: '0.9rem', fontWeight: 500 }}
        >
          {submitError}
        </Alert>
      </Snackbar>

    </Dialog>
  );
};

export default DisposeAssetModal;